/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useState, useEffect } from 'react';
import { EntryPointContainer } from 'react-relay';
import { useIsHydrateRenderMode } from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { usePageLayoutEntryPoint } from '@jira/platform__entry-points-page-layout-entry-point/src/index.tsx';
import { LEFT_SIDEBAR } from '@jira/platform__entry-points-page-layout-fields/src/index.tsx';
import { EntryPointReferencePlaceholder } from '@jira/platform__entry-points-point-placeholder/src/index.tsx';
import { Skeleton as ProjectSettingsSkeleton } from '@jira/navigation-apps__sidebar-nav4-sidebars-settings-project-common/src/common/ui/skeleton/index.tsx';
import { useCurrentRoute } from '@jira/platform__utils-router/src/index.tsx';
import { NavigateBackProvider } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-back-button/src/controllers/navigate-back-context/index.tsx';
import { GLOBAL } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-layer.tsx';
import { SIDEBAR } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/navigation-container.tsx';
import { V4 } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/nav-state.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { ContextualAnalyticsData } from '@atlassian/jira-product-analytics-bridge';
import { SidebarSelector } from '../regular/index.tsx';
import { getNav4SidebarId, useGetParamsForGetNav4SidebarId } from '../../common/logic.tsx';

export function SidebarEntryPointSelector(): React.JSX.Element {
	const currentRoute = useCurrentRoute();
	const paramsForGetNav4SidebarId = useGetParamsForGetNav4SidebarId();
	const currentSidebarId = getNav4SidebarId(paramsForGetNav4SidebarId);
	const currentEntryPointRef = usePageLayoutEntryPoint(LEFT_SIDEBAR);
	const isPageHydrating = useIsHydrateRenderMode();

	const [sidebarConfig, setSidebarConfig] = useState(() => {
		// We want to avoid switching between the SidebarSelector and the sidebar entryPoint as we navigate through the SPA.
		// To do that, we need to keep using the __initial__ sidebar entryPoint if we haven't changed sidebars.
		// When we do switch sidebars, keep using SidebarSelector from that point onwards (for now).
		// When all of the sidebars are using entryPoints, we can deprecate the SidebarSelector.
		const attributes = {
			navigationLayer: GLOBAL,
			navigationContainer: SIDEBAR,
			navState: V4,
		};

		return {
			Component: () =>
				currentEntryPointRef && currentRoute.navigation?.sidebarId === currentSidebarId ? (
					<UFOSegment name="nav4-sidebar-entrypoint">
						<JSErrorBoundary
							fallback="flag"
							id="nav4-sidebar-entrypoint"
							packageName="jira-page-container-v2"
						>
							<NavigateBackProvider>
								<ContextualAnalyticsData attributes={attributes}>
									<EntryPointReferencePlaceholder
										name="nav4-sidebar-entrypoint-placeholder"
										fallback={<ProjectSettingsSkeleton />}
										entryPointReference={currentEntryPointRef}
										isSSRPlaceholderEnabled={
											!isPageHydrating
												? currentRoute.entryPointOptions?.isSSRPlaceholderEnabled
												: undefined
										}
									>
										<EntryPointContainer
											entryPointReference={currentEntryPointRef}
											// oxlint-disable-next-line typescript/consistent-type-assertions
											props={{} as never}
										/>
									</EntryPointReferencePlaceholder>
								</ContextualAnalyticsData>
							</NavigateBackProvider>
						</JSErrorBoundary>
					</UFOSegment>
				) : (
					<SidebarSelector />
				),
			sidebarId: currentSidebarId,
		};
	});

	useEffect(() => {
		if (sidebarConfig.sidebarId !== currentSidebarId) {
			setSidebarConfig({ Component: SidebarSelector, sidebarId: currentSidebarId });
		}
	}, [currentSidebarId, sidebarConfig.sidebarId]);

	return <sidebarConfig.Component />;
}
